// ScrollableScreen
import ScrollableScreen from '@/components/ui/ScrollableScreen'
import Box from '@/components/ui/Box'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// React
import { useEffect, useState } from 'react'

// Mantine
import { Button, Anchor } from '@mantine/core'

// Icons
import { IconExternalLink, IconCloudDownload } from '@tabler/icons-react'

// Status
import { getInvoiceStatus, getEstimateStatus } from '@/lib/retailerStatuses'

// Next
import { Link, useParams } from 'react-router-dom'
// Dayjs
import dayjs from 'dayjs'

// UI
import Badge from '@/components/ui/Badge'

export default function RetailerInvoiceDisplay() {
    // API
    const { requestGet } = useAxiosApi()

    // Get invoice ID
    const { id: invoiceId } = useParams()

    // Load invoice
    const [invoice, setInvoice] = useState(null)
    const invoiceStatus = getInvoiceStatus(invoice?.status)

    const loadInvoice = async () => {
        const { data } = await requestGet(`/retailer-invoices/${invoiceId}`)

        setInvoice(data)
    }

    useEffect(() => {
        loadInvoice()
    }, [invoiceId])

    // Breadcrumbs
    const breadcrumbs = [
        { label: 'Factures', href: '/retailer_invoices' },
        { label: invoice?.number },
    ]

    // Right section
    const RightSection = (
        <div className="flex gap-2 items-center">
            <Badge color={invoiceStatus.badgeColor}>
                {invoiceStatus.label}
            </Badge>
        </div>
    )

    const PDFBoxRightSection = (
        <div className="flex gap-2 items-center">
            <Button
                variant="default"
                component="a"
                href={invoice?.pdf_link}
                target="_blank"
                leftSection={<IconExternalLink size="1rem" />}
            >
                Ouvrir le PDF
            </Button>
        </div>
    )

    const estimateStatus = getEstimateStatus(invoice?.retailer_estimate?.status)

    if (!invoice) {
        return null
    }

    return (
        <ScrollableScreen breadcrumbs={breadcrumbs} rightSection={RightSection}>
            <div className="p-5 flex items-center justify-center">
                <div className="flex flex-col gap-5 w-full max-w-[900px]">
                    <Box title={invoice.number} rightSection={RightSection}>
                        <div className="flex flex-col items-start gap-5">
                            <div className="flex flex-col items-start">
                                <span>Informations</span>
                                <div className="flex flex-col items-start text-gray-500 text-sm font-normal">
                                    <span>
                                        {dayjs(invoice?.date).format('ll')}
                                    </span>

                                    <span>
                                        {Intl.NumberFormat('fr-FR', {
                                            style: 'currency',
                                            currency: 'EUR',
                                        }).format(
                                            invoice?.amounts.total_incl_tax
                                        )}
                                    </span>
                                </div>
                            </div>

                            <div className="flex flex-col items-start">
                                <span>Revendeur</span>

                                <Anchor
                                    href={`/retailers/${invoice.retailer.id}`}
                                >
                                    {invoice.retailer.name}
                                </Anchor>
                            </div>

                            <div className="flex flex-col items-start w-full gap-2">
                                <span>Devis</span>

                                {invoice.retailer_estimate ? (
                                    <Link
                                        className="grid grid-cols-[1fr_110px_110px_110px] items-center w-full  border px-3 py-2 rounded-lg hover:bg-gray-50"
                                        to={`/retailer_estimates/${invoice.retailer_estimate.id}`}
                                    >
                                        <Anchor component="span">
                                            {invoice.retailer_estimate.number}
                                        </Anchor>

                                        <span className="text-gray-500 text-sm">
                                            {Intl.NumberFormat('fr-FR', {
                                                style: 'currency',
                                                currency: 'EUR',
                                            }).format(
                                                invoice.retailer_estimate
                                                    .amounts.total
                                            )}
                                        </span>

                                        <span className="text-gray-500 text-sm">
                                            {dayjs(
                                                invoice.retailer_estimate.date
                                            ).format('ll')}
                                        </span>

                                        <div className="flex justify-end">
                                            <Badge
                                                color={
                                                    estimateStatus.badgeColor
                                                }
                                                size="xs"
                                            >
                                                {estimateStatus.label}
                                            </Badge>
                                        </div>
                                    </Link>
                                ) : (
                                    <span className="text-gray-500 text-sm">
                                        Aucun devis lié
                                    </span>
                                )}
                            </div>
                        </div>
                    </Box>

                    <Box title="Fichier PDF" rightSection={PDFBoxRightSection}>
                        <embed
                            src={invoice.pdf_link}
                            className="w-full aspect-[0.7]"
                            type="application/pdf"
                        />
                    </Box>
                </div>
            </div>
        </ScrollableScreen>
    )
}
